import { STARMAP_URL, API_BASE_URL, API_TIMEOUT } from '../config';

// Flag para evitar procesar el token de la URL múltiples veces
let urlTokenProcessed = false;
// Cache de validación del token para evitar múltiples llamadas
let tokenValidationCache: { token: string; valid: boolean; timestamp: number } | null = null;
const VALIDATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export const authService = {
  /**
   * Valida el token contra la API
   * @param token - Token JWT a validar
   * @returns Promise con el resultado de la validación
   */
  async validateToken(token: string): Promise<{ valid: boolean; user?: string; error?: string }> {
    // Verificar cache
    if (tokenValidationCache && tokenValidationCache.token === token) {
      const now = Date.now();
      if (now - tokenValidationCache.timestamp < VALIDATION_CACHE_DURATION) {
        return { valid: tokenValidationCache.valid };
      }
    }

    try {
      // El endpoint es /api/auth/validate-token
      // API_BASE_URL ya incluye /api, así que solo agregamos /auth/validate-token
      const url = `${API_BASE_URL}/auth/validate-token`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-API-Key': token  // El token JWT (corvus_token) se envía en este header para validación
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ mensaje: 'Error desconocido' }));
        const errorMessage = errorData.mensaje || `Error ${response.status}: ${response.statusText}`;
        
        // Actualizar cache con resultado negativo
        tokenValidationCache = { token, valid: false, timestamp: Date.now() };
        
        return { valid: false, error: errorMessage };
      }

      const data = await response.json();

      if (data.valid === true) {
        // Actualizar cache con resultado positivo
        tokenValidationCache = { token, valid: true, timestamp: Date.now() };
        return { valid: true, user: data.user };
      } else {
        // Actualizar cache con resultado negativo
        tokenValidationCache = { token, valid: false, timestamp: Date.now() };
        return { valid: false, error: data.mensaje || 'Token inválido' };
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('⏱️ Timeout validando token');
        return { valid: false, error: 'Timeout validando token' };
      }
      
      console.error('Error validando token:', error);
      return { valid: false, error: error instanceof Error ? error.message : 'Error desconocido' };
    }
  },
  /**
   * Procesa el token desde la URL y lo guarda en sessionStorage
   * @returns true si se procesó un token, false si no había token en la URL
   */
  async processTokenFromUrl(): Promise<boolean> {
    // Si ya se procesó el token de la URL, no volver a procesarlo
    if (urlTokenProcessed) {
      return false;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      // Buscar el token en diferentes parámetros de la URL, incluyendo X-API-Key
      const token = urlParams.get('token') || 
                    urlParams.get('corvus_token') || 
                    urlParams.get('auth_token') ||
                    urlParams.get('X-API-Key') ||
                    urlParams.get('x-api-key') ||
                    urlParams.get('api_key');
      
      if (!token) {
        return false;
      }

      // Marcar como procesado antes de continuar
      urlTokenProcessed = true;

      // Validar que el token tenga formato JWT básico (tiene 3 partes separadas por puntos)
      if (!token.includes('.') || token.split('.').length !== 3) {
        console.warn('⚠️ Token de URL no tiene formato JWT válido');
        return false;
      }

      // Decodificar el token para validar estructura básica
      try {
        const payload = token.split('.')[1];
        const decodedData = JSON.parse(atob(payload));
        
        // Validar que tenga campos básicos
        if (!decodedData.user && !decodedData.handler) {
          console.warn('⚠️ Token de URL no tiene estructura válida');
          return false;
        }

        // Verificar expiración
        const now = Math.floor(Date.now() / 1000);
        if (decodedData.exp && decodedData.exp < now) {
          console.warn('⚠️ Token de URL está expirado');
          return false;
        }

        // Validar el token contra la API antes de guardarlo
        console.log('🔍 Validando token desde URL contra la API...');
        const validation = await this.validateToken(token);
        
        if (!validation.valid) {
          console.error('❌ Token inválido según la API:', validation.error);
          urlTokenProcessed = false;
          return false;
        }

        console.log('✅ Token válido según la API, guardando en sessionStorage...');
        // Guardar token en sessionStorage como corvus_token
        sessionStorage.setItem('corvus_token', token);

        // Guardar datos del usuario si están disponibles
        const userData = {
          user: decodedData.user || validation.user || '',
          handler: decodedData.handler,
          admin: decodedData.admin || false,
          email: decodedData.email || '',
          sistema_origen: decodedData.sistema_origen || '',
          planeta_origen: decodedData.planeta_origen || '',
          organizaciones: decodedData.organizaciones || [],
          capitan: decodedData.capitan || decodedData.Capitan || false,
          nick: decodedData.nick || '',
          reputacion_corvus: decodedData.reputacion_corvus || 0,
          reputacion_stars: decodedData.reputacion_stars || 0
        };
        sessionStorage.setItem('user_data', JSON.stringify(userData));

        // Limpiar el token de la URL sin recargar la página
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('token');
        newUrl.searchParams.delete('corvus_token');
        newUrl.searchParams.delete('auth_token');
        newUrl.searchParams.delete('X-API-Key');
        newUrl.searchParams.delete('x-api-key');
        newUrl.searchParams.delete('api_key');
        window.history.replaceState({}, '', newUrl.toString());

        console.log('✅ Token procesado desde URL y guardado en sessionStorage');
        return true;
      } catch (decodeError) {
        console.error('Error decodificando token de URL:', decodeError);
        urlTokenProcessed = false;
        return false;
      }
    } catch (error) {
      console.error('Error procesando token de URL:', error);
      urlTokenProcessed = false;
      return false;
    }
  },

  async isAuthenticated(): Promise<boolean> {
    // Primero intentar procesar token de la URL si existe
    await this.processTokenFromUrl();

    // Verificar usando corvus_token de sessionStorage (httpOnly desde corvusnocta.org)
    try {
      // Intentar diferentes variaciones del nombre del token
      const possibleKeys = ['corvus_token', 'CorvusToken', 'CORVUS_TOKEN', 'token', 'auth_token'];
      let token: string | null = null;
      let foundKey: string | null = null;

      for (const key of possibleKeys) {
        const value = sessionStorage.getItem(key);
        if (value) {
          token = value;
          foundKey = key;
          break;
        }
      }

      // Si no se encuentra con el nombre estándar, buscar cualquier clave que contenga 'token' o 'corvus'
      if (!token) {
        const allKeys = Object.keys(sessionStorage);
        for (const key of allKeys) {
          if (key.toLowerCase().includes('token') || key.toLowerCase().includes('corvus')) {
            token = sessionStorage.getItem(key);
            foundKey = key;
            if (token) break;
          }
        }
      }

      // Si aún no hay token, buscar en la URL (incluyendo X-API-Key)
      if (!token) {
        const urlParams = new URLSearchParams(window.location.search);
        token = urlParams.get('token') || 
                urlParams.get('corvus_token') || 
                urlParams.get('auth_token') ||
                urlParams.get('X-API-Key') ||
                urlParams.get('x-api-key') ||
                urlParams.get('api_key');
        if (token) {
          foundKey = 'url';
        }
      }

      // Debug: verificar qué hay en sessionStorage
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Verificando autenticación:', {
          token: token ? `${token.substring(0, 20)}...` : 'null',
          foundKey: foundKey,
          allKeys: Object.keys(sessionStorage),
          corvus_token_exists: !!sessionStorage.getItem('corvus_token')
        });
      }

      return !!token;
    } catch (error) {
      console.error('Error accediendo a sessionStorage:', error);
      return false;
    }
  },

  redirectToStarmap() {
    // Guardar la URL actual para volver después del login
    const currentUrl = window.location.href;
    sessionStorage.setItem('return_url', currentUrl);
    // Redirigir a starmap.corvusnocta.org para autenticación
    window.location.href = STARMAP_URL;
  },

  logout() {
    // Limpiar sessionStorage
    sessionStorage.removeItem('corvus_token');
    sessionStorage.removeItem('user_data');
    // Redirigir a starmap para cerrar sesión
    window.location.href = STARMAP_URL;
  },

  getUserData() {
    // Leer user_data de sessionStorage (guardado por corvusnocta.org)
    const userData = sessionStorage.getItem('user_data');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user_data:', error);
        return null;
      }
    }
    return null;
  },

  async getToken(): Promise<string | null> {
    // Primero intentar procesar token de la URL si existe
    await this.processTokenFromUrl();

    // Leer corvus_token de sessionStorage (httpOnly desde corvusnocta.org)
    try {
      // Intentar diferentes variaciones del nombre del token
      const possibleKeys = ['corvus_token', 'CorvusToken', 'CORVUS_TOKEN', 'token', 'auth_token'];
      
      for (const key of possibleKeys) {
        const value = sessionStorage.getItem(key);
        if (value) {
          return value;
        }
      }

      // Si no se encuentra con el nombre estándar, buscar cualquier clave que contenga 'token' o 'corvus'
      const allKeys = Object.keys(sessionStorage);
      for (const key of allKeys) {
        if (key.toLowerCase().includes('token') || key.toLowerCase().includes('corvus')) {
          const value = sessionStorage.getItem(key);
          if (value) return value;
        }
      }

      // Si aún no hay token, buscar en la URL (incluyendo X-API-Key)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token') || 
                       urlParams.get('corvus_token') || 
                       urlParams.get('auth_token') ||
                       urlParams.get('X-API-Key') ||
                       urlParams.get('x-api-key') ||
                       urlParams.get('api_key');
      if (urlToken) {
        // Procesar y guardar el token de la URL
        await this.processTokenFromUrl();
        return sessionStorage.getItem('corvus_token');
      }

      return null;
    } catch (error) {
      console.error('Error leyendo token de sessionStorage:', error);
      return null;
    }
  },

  // Función de utilidad para debug: listar todas las claves de sessionStorage
  debugSessionStorage(): void {
    console.log('📦 Contenido de sessionStorage:');
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      const value = sessionStorage.getItem(key);
      console.log(`  ${key}:`, value ? (value.length > 50 ? `${value.substring(0, 50)}...` : value) : 'null');
    });
    console.log('🔑 Buscando corvus_token:', sessionStorage.getItem('corvus_token') ? '✅ Encontrado' : '❌ No encontrado');
  }
}; 